// Night glow wash geometry for Volcanoes.tsx: ONE preallocated mesh holding a
// small hex fan (5 rings: 91 verts, 150 triangles) per
// neighbour of every lit volcano. Each vertex sits on the neighbour's real
// tile surface (tileSurfaceY + reliefAt + a crag margin), so hills and
// mountain relief no longer poke above a flat sheet. Rebuilt only when the
// volcano set changes; buffers are fixed-size (no realloc) and only the draw
// range moves.

import * as THREE from 'three';
import { reliefAt, tileSurfaceY } from './elevation.js';
import { EDGE_NEIGHBOURS } from './terrainEdges.js';
import { CRAG_AMP, FLAT_CRAG } from './tileGeometry.js';
import { type Volcano, volcanoLevel } from './volcano.js';

/** Ring circumradii of the fan (centre vertex is ring 0). */
export const GLOW_RINGS: readonly number[] = [0.22, 0.45, 0.68, 0.85, 0.97];
export const GLOW_PER_VOLCANO = 6;
const VERTS = 1 + GLOW_RINGS.reduce((n, _, i) => n + 6 * (i + 1), 0);
/** Triangles between consecutive rings = verts(ring k) + verts(ring k+1). */
const TRIS = 6 + GLOW_RINGS.slice(0, -1).reduce((n, _, i) => n + 6 * (i + 1) + 6 * (i + 2), 0);
export const GLOW_VERTS_PER_TILE = VERTS;
export const GLOW_TRIS_PER_TILE = TRIS;

const SQ3 = Math.sqrt(3);

/** Unit-circumradius pointy-top hex corner c (0..5) as [x, z]. */
function corner(c: number): [number, number] {
  const a = Math.PI / 6 + (c * Math.PI) / 3;
  return [Math.cos(a), Math.sin(a)];
}

/** Local [x, z] offsets (from the tile centre) of every fan vertex, ring-major. */
export const GLOW_LOCAL: ReadonlyArray<readonly [number, number]> = (() => {
  const out: Array<[number, number]> = [[0, 0]];
  GLOW_RINGS.forEach((radius, ri) => {
    const n = 6 * (ri + 1);
    const k = ri + 1;
    for (let j = 0; j < n; j++) {
      const c = Math.floor(j / k);
      const t = (j % k) / k;
      const [x0, z0] = corner(c);
      const [x1, z1] = corner((c + 1) % 6);
      out.push([(x0 + (x1 - x0) * t) * radius, (z0 + (z1 - z0) * t) * radius]);
    }
  });
  return out;
})();

/** Fan triangle indices for one tile (relative to the tile's first vertex). */
export const GLOW_INDEX: ReadonlyArray<number> = (() => {
  const idx: number[] = [];
  const start = (ring: number) => (ring === 0 ? 0 : 1 + 3 * (ring - 1) * ring);
  // Ring 0 (centre) -> ring 1.
  for (let j = 0; j < 6; j++) idx.push(0, start(1) + j, start(1) + ((j + 1) % 6));
  for (let ring = 1; ring < GLOW_RINGS.length; ring++) {
    const n = 6 * ring;
    const m = 6 * (ring + 1);
    let i = 0;
    let j = 0;
    // Merge-walk both rings by angular fraction: emits n + m triangles.
    while (i < n || j < m) {
      const a = start(ring) + (i % n);
      const b = start(ring + 1) + (j % m);
      const advanceA = j >= m || (i < n && (i + 1) / n <= (j + 1) / m);
      if (advanceA) {
        idx.push(a, start(ring) + ((i + 1) % n), b);
        i++;
      } else {
        idx.push(a, b, start(ring + 1) + ((j + 1) % m));
        j++;
      }
    }
  }
  return idx;
})();

export interface GlowBuffers {
  readonly geometry: THREE.BufferGeometry;
  readonly capacityTiles: number;
}

/** Preallocate the glow mesh for up to `maxTiles` neighbour fans. */
export function createGlowGeometry(maxTiles: number): GlowBuffers {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(maxTiles * VERTS * 3), 3));
  g.setAttribute('aRel', new THREE.BufferAttribute(new Float32Array(maxTiles * VERTS * 2), 2));
  g.setAttribute('aState', new THREE.BufferAttribute(new Float32Array(maxTiles * VERTS), 1));
  const index = new Uint16Array(maxTiles * TRIS * 3);
  for (let t = 0; t < maxTiles; t++) {
    for (let k = 0; k < GLOW_INDEX.length; k++)
      index[t * GLOW_INDEX.length + k] = t * VERTS + GLOW_INDEX[k]!;
  }
  g.setIndex(new THREE.BufferAttribute(index, 1));
  g.setDrawRange(0, 0);
  return { geometry: g, capacityTiles: maxTiles };
}

/**
 * Height margin over the smooth surface: the vertex shader displaces the
 * tile's raised rings by up to ~0.65 * the biome's jag amplitude.
 */
export function glowLift(biome: string): number {
  return 0.025 + 0.45 * (CRAG_AMP[biome] ?? FLAT_CRAG)[0];
}

const centre = (q: number, r: number): [number, number] => [SQ3 * (q + r / 2), 1.5 * r];

/**
 * Write every lit (active / erupting) volcano's neighbour fans into the
 * preallocated buffers; returns the number of tiles written.
 */
export function fillGlowGeometry(
  buffers: GlowBuffers,
  vols: readonly Volcano[],
  biomeMap: Map<string, string>,
): number {
  const { geometry, capacityTiles } = buffers;
  const pos = geometry.getAttribute('position') as THREE.BufferAttribute;
  const rel = geometry.getAttribute('aRel') as THREE.BufferAttribute;
  const st = geometry.getAttribute('aState') as THREE.BufferAttribute;
  let tile = 0;
  for (const v of vols) {
    const lvl = volcanoLevel(v.state);
    if (lvl < 1) continue;
    const [vx, vz] = centre(v.q, v.r);
    for (const [dq, dr] of EDGE_NEIGHBOURS.slice(0, GLOW_PER_VOLCANO)) {
      if (tile >= capacityTiles) break;
      const nq = v.q + dq;
      const nr = v.r + dr;
      const [nx, nz] = centre(nq, nr);
      const biome = biomeMap.get(`${nq},${nr}`) ?? 'plains';
      const base = tileSurfaceY(nq, nr, biomeMap) + glowLift(biome);
      for (let k = 0; k < VERTS; k++) {
        const [lx, lz] = GLOW_LOCAL[k]!;
        const i = tile * VERTS + k;
        pos.setXYZ(i, nx + lx, base + reliefAt(biome, lx, lz), nz + lz);
        rel.setXY(i, nx + lx - vx, nz + lz - vz);
        st.setX(i, lvl);
      }
      tile++;
    }
  }
  pos.needsUpdate = true;
  rel.needsUpdate = true;
  st.needsUpdate = true;
  geometry.setDrawRange(0, tile * TRIS * 3);
  return tile;
}
